import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { History, PhoneCall } from 'lucide-react';
import { tr } from '@/lib/i18n-core';
import { get } from '@/lib/api';
import { describeError, useAuth } from '@/lib/auth';
import { dateOnly, dateTime, money } from '@/lib/utils';
import { Alert, Button, Spinner } from '@/components/ui/primitives';
import { ShellEmpty, ShellPanel, ShellSectionHeading } from '@/components/clinical-shell/ShellPanel';
import { admissionPath, type Admission, type DuesNote } from '../types';

export function DuesSection({ admission }: { admission: Admission }) {
  const { can } = useAuth();
  const notes = useQuery({
    queryKey: ['dues-notes', admission.id],
    queryFn: () => get<DuesNote[]>(`/ipd/admissions/${admission.id}/dues-notes`),
  });
  const owingMinor = admission.account.balanceMinor < 0 ? Math.abs(admission.account.balanceMinor) : 0;
  const followUpTo = `/ipd/dues/${admission.id}/follow-up?returnTo=${encodeURIComponent(admissionPath(admission.id, 'dues'))}`;

  return (
    <>
      <ShellSectionHeading
        icon={PhoneCall}
        title={tr('ipd', 'ws_nav_dues')}
        description={owingMinor > 0 ? tr('ipd', 'ws_owing_x', { amount: money(owingMinor) }) : tr('ipd', 'ws_nothing_owing')}
        actions={
          can('dues.manage') ? (
            <Link to={followUpTo}>
              <Button size="sm">
                <PhoneCall className="h-4 w-4" aria-hidden /> {tr('ipd', 'ws_record_follow_up')}
              </Button>
            </Link>
          ) : null
        }
      />
      {admission.status === 'absconded' && admission.account.depositedMinor > 0 && (
        <Alert variant="warning">
          {tr('ipd', 'this_patient_left_without_notice')} {money(admission.account.depositedMinor)} {tr('ipd', 'of_their_advance_was_not_refunded_it_is_kept_as')}
        </Alert>
      )}
      <ShellPanel icon={History} title={tr('ipd', 'notes')}>
        {notes.isLoading && <Spinner />}
        {notes.isError && <Alert variant="destructive">{describeError(notes.error)}</Alert>}
        {notes.data?.length === 0 && <ShellEmpty>{tr('ipd', 'nobody_has_followed_up_on_this_yet')}</ShellEmpty>}
        <ul className="space-y-2">
          {notes.data?.map((entry) => (
            <li key={entry.id} className="rounded-xl border px-3 py-2 text-sm">
              <p className="whitespace-pre-wrap">{entry.note}</p>
              <p className="mt-1 text-xs text-muted-foreground">
                {[
                  entry.author ?? tr('ipd', 'someone'),
                  dateTime(entry.createdAt),
                  entry.promisedDate ? tr('ipd', 'ws_promised_x', { date: dateOnly(entry.promisedDate) }) : null,
                  entry.promisedAmountMinor ? money(entry.promisedAmountMinor) : null,
                ]
                  .filter(Boolean)
                  .join(' · ')}
              </p>
            </li>
          ))}
        </ul>
      </ShellPanel>
    </>
  );
}
